// src/redux/driversSlice.ts
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  doc, getDoc, setDoc, deleteDoc, collection, addDoc,
  query, where, onSnapshot, serverTimestamp,
  orderBy, limit, getDocs, updateDoc, writeBatch,
} from "firebase/firestore";
import { ref as dbRef, onValue, off } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { db, rtdb, storage } from "../firebase";
import type { AppDispatch, RootState } from "./store";

/* ─────────────────────────────────────────────────────────────────────────── */
/* TYPES                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

export interface DriverProfile {
  listingId:  string;   // random Firestore doc ID
  driverId:   string;   // current user UID
  hostId:     string;   // current user UID (same as driverId)
  operatorId: string;
  rideType?:     number | null;
  vehicleType?:  number | null;
  vehicleSize?:  string;
  businessType?: number | null;
  brand?:        number | null;
  classType?:    number | null;
  carSize?:      number | null;
  color?:        number | null;
  transmission?: number | null;
  fuelType?:     number | null;
  model?:        string;
  licensePlate?: string;
  year?:         string;
  capacity?:     string;
  description?:  string;
  photos?:       string[];
  rate?:            number;
  rateType?:        "perSurge" | "perPerson" | "perKm" | "perDay" | "perCar" | "perSeat";
  extras?:          Record<string, number>;
  extraCharges?:    number;
  discountApplied?: Record<string, boolean>;
  rideSharing?: boolean;
  city?:    string;
  street?:  string;
  country?: string;
  isOnline?:        boolean;
  isBusy?:          boolean;
  isAvailable?:     boolean;
  serviceRadiusKm?: number;
  operationHours?:  string;
  allowParcel?: boolean;
  // Uber-Fleet-style "which vehicle am I driving right now" flag. Only one
  // listing per driverId should be true at any given time.
  isSelected?:  boolean;
  isPublished:  boolean;
  isFlagged:    boolean;
  isComplete:   boolean;
  isReviewed:   boolean;
  inReview:     boolean;
  isDraft:      boolean;
  isPromoted?:  boolean;
  insured?:     boolean;
  agreePolicy?: boolean;
  progress?:    string;
  createdAt?: number | null;
  updatedAt?: number | null;
  [key: string]: any;
}

export interface ActiveTrip {
  tripId:         string;
  riderId:        string;
  driverId:       string | null;
  listingId:      string | null;
  status:         string;
  pickupLat:      number;
  pickupLng:      number;
  dropoffLat:     number | null;
  dropoffLng:     number | null;
  pickupAddress:  string;
  dropoffAddress: string;
  distanceKm:     number;
  rateType:       string;
  totalCharged:   number;
  guestFee:       number;
  hostFee:        number;
  driverProfit:   number;
  platformProfit: number;
  isPaid:         boolean;
  isConfirmed:    boolean;
  isCancelled:    boolean;
  etaMinutes?:    number;
}

export interface ActiveParcel {
  parcelId:        string;
  status:          string;
  pickup_address:  string;
  dropoff_address: string;
  distanceKm:      number;
  courierPayout:   number;
  is_fragile:      boolean;
  is_urgent:       boolean;
  weight_kg:       number;
  etaMinutes?:     number;
}

interface DriversState {
  currentDriver:        DriverProfile | null;
  allDrivers:           DriverProfile[];
  listings:             DriverProfile[];
  activeTrip:           ActiveTrip | null;
  activeParcel:         ActiveParcel | null;
  pendingTripRequest:   any | null;
  pendingParcelRequest: any | null;
  loading:              boolean;
  error:                string | null;
}

const initialState: DriversState = {
  currentDriver:        null,
  allDrivers:           [],
  listings:             [],
  activeTrip:           null,
  activeParcel:         null,
  pendingTripRequest:   null,
  pendingParcelRequest: null,
  loading:              false,
  error:                null,
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* HELPERS                                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

const toMillis = (val: any): number | null => {
  if (!val) return null;
  if (typeof val?.toMillis === "function") return val.toMillis();
  if (val?._seconds !== undefined) return val._seconds * 1000 + (val._nanoseconds ?? 0) / 1e6;
  if (typeof val === "number") return val;
  return null;
};

const parseDriver = (docId: string, raw: any): DriverProfile => ({
  listingId:  raw.listingId  ?? docId,
  driverId:   raw.driverId   ?? raw.hostId ?? docId,
  hostId:     raw.hostId     ?? raw.driverId ?? docId,
  operatorId: raw.operatorId ?? "",
  rideType:     raw.rideType     ?? null,
  vehicleSize:  raw.vehicleSize  ?? null,
  vehicleType:  raw.vehicleType  ?? null,
  brand:        raw.brand        ?? null,
  classType:    raw.classType    ?? null,
  carSize:      raw.carSize      ?? null,
  color:        raw.color        ?? null,
  transmission: raw.transmission ?? null,
  fuelType:     raw.fuelType     ?? null,
  model:        raw.model        ?? "",
  licensePlate: raw.licensePlate ?? "",
  year:         raw.year         ?? "",
  capacity:     raw.capacity     ?? "",
  description:  raw.description  ?? "",
  photos:       raw.photos       ?? [],
  rate:            raw.rate            ?? null,
  rateType:        raw.rateType        ?? "perSurge",
  extras:          raw.extras          ?? {},
  extraCharges:    raw.extraCharges    ?? 0,
  discountApplied: raw.discountApplied ?? {},
  businessType: raw.businessTypeId ?? raw.businessType ?? null,
  rideSharing:  raw.rideSharing    ?? false,
  city:    raw.address?.city    ?? raw.city    ?? "",
  street:  raw.address?.street  ?? raw.street  ?? "",
  country: raw.address?.country ?? raw.country ?? "",
  isOnline:        raw.isOnline        ?? false,
  isBusy:          raw.isBusy          ?? false,
  isAvailable:     raw.isAvailable     ?? false,
  serviceRadiusKm: raw.serviceRadiusKm ?? 0,
  operationHours:  raw.operationHours  ?? "",
  allowParcel:     raw.allowParcel     ?? false,
  insured:         raw.insured         ?? false,
  agreePolicy:     raw.agreePolicy     ?? false,
  progress:        raw.progress        ?? "",
  isSelected:  raw.isSelected  ?? false,
  isPublished: raw.isPublished ?? false,
  isFlagged:   raw.isFlagged   ?? false,
  isComplete:  raw.isComplete  ?? false,
  isReviewed:  raw.isReviewed  ?? false,
  inReview:    raw.inReview    ?? false,
  isDraft:     raw.isDraft     ?? true,
  isPromoted:  raw.isPromoted  ?? false,
  createdAt: toMillis(raw.createdAt),
  updatedAt: toMillis(raw.updatedAt),
});

const toActiveTrip = (data: any): ActiveTrip => ({ ...data } as ActiveTrip);

/* ─────────────────────────────────────────────────────────────────────────── */
/* THUNKS                                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * upsertDriver
 *
 * CREATE: no listingId → addDoc generates random ID, then we write listingId
 *         back as a field (mirrors shortstaysSlice exactly).
 * UPDATE: listingId supplied → updateDoc on that doc.
 *
 * driverId and hostId are always the authenticated user's UID.
 *
 * The very first vehicle a driver ever creates is automatically marked
 * isSelected: true (so there's always an "active" vehicle, Uber-Fleet style).
 */
export const upsertDriver = createAsyncThunk<
  DriverProfile,
  { listingId?: string; data: Partial<DriverProfile> },
  { rejectValue: string }
>(
  "drivers/upsertDriver",
  async ({ listingId, data }, { rejectWithValue }) => {
    try {
      const uid = getAuth().currentUser?.uid;
      if (!uid) throw new Error("Not authenticated");

      // ── UPDATE EXISTING ──────────────────────────────────────────────────
      if (listingId) {
        const docRef = doc(db, "riders", listingId);
        await updateDoc(docRef, {
          ...data,
          listingId,
          driverId:  uid,
          hostId:    uid,
          updatedAt: serverTimestamp(),
        });
        const snap = await getDoc(docRef);
        return parseDriver(snap.id, snap.data());
      }

      // ── CREATE NEW ───────────────────────────────────────────────────────
      const existingSnap = await getDocs(
        query(collection(db, "riders"), where("driverId", "==", uid))
      );
      const isFirstVehicle = existingSnap.empty;

      const newDocRef = await addDoc(collection(db, "riders"), {
        ...data,
        driverId:    uid,
        hostId:      uid,
        isSelected:  isFirstVehicle, // first vehicle ever added becomes active
        isPublished: false,
        isFlagged:   false,
        isComplete:  false,
        isReviewed:  false,
        inReview:    false,
        isDraft:     true,
        createdAt:   serverTimestamp(),
        updatedAt:   serverTimestamp(),
      });

      // Write listingId back as a field — mirrors shortstay pattern exactly
      await updateDoc(newDocRef, { listingId: newDocRef.id });

      const snap = await getDoc(newDocRef);
      return parseDriver(snap.id, snap.data());
    } catch (e: any) {
      return rejectWithValue(e.message ?? "Failed to upsert driver");
    }
  }
);

/**
 * upsertDriverPhotos
 * Takes listingId (random doc ID) — mirrors upsertShortstayPhoto.
 */
export const upsertDriverPhotos = createAsyncThunk<
  string[],
  { listingId: string; photos: File[] },
  { state: RootState; rejectValue: string }
>(
  "drivers/upsertDriverPhotos",
  async ({ listingId, photos }, { getState, rejectWithValue }) => {
    try {
      const uid = getAuth().currentUser?.uid;
      if (!uid) throw new Error("Not authenticated");

      const uploaded: string[] = [];
      for (let i = 0; i < photos.length; i++) {
        const file = photos[i];
        const name = file.name || `photo-${Date.now()}-${i}`;
        const sRef = storageRef(storage, `riders/${listingId}/photos/${name}`);
        await uploadBytes(sRef, file);
        uploaded.push(await getDownloadURL(sRef));
      }

      const existing = getState().drivers.currentDriver?.photos ?? [];
      const merged   = [...existing, ...uploaded];

      await updateDoc(doc(db, "riders", listingId), {
        photos:    merged,
        updatedAt: serverTimestamp(),
      });

      return merged;
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

/**
 * deleteDriver — deletes listing by listingId (random doc ID).
 * Mirrors deleteShortstay.
 */
export const deleteDriver = createAsyncThunk(
  "drivers/deleteDriver",
  async (listingId: string, { rejectWithValue }) => {
    try {
      const snap = await getDoc(doc(db, "riders", listingId));
      if (!snap.exists()) throw new Error("Listing not found");
      for (const url of (snap.data()?.photos ?? []) as string[]) {
        try { await deleteObject(storageRef(storage, url)); } catch {}
      }
      await deleteDoc(doc(db, "riders", listingId));
      return listingId;
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

/**
 * setActiveVehicle
 *
 * Uber-Fleet-style "which vehicle am I driving right now" switch. Marks
 * `listingId` as isSelected: true and every other listing owned by
 * `driverId` as isSelected: false, in a single atomic batch write.
 */
export const setActiveVehicle = createAsyncThunk<
  { driverId: string; listingId: string },
  { driverId: string; listingId: string },
  { rejectValue: string }
>(
  "drivers/setActiveVehicle",
  async ({ driverId, listingId }, { rejectWithValue }) => {
    try {
      const snap  = await getDocs(query(collection(db, "riders"), where("driverId", "==", driverId)));
      const batch = writeBatch(db);
      snap.docs.forEach((d) => {
        batch.update(d.ref, {
          isSelected: d.id === listingId,
          updatedAt:  serverTimestamp(),
        });
      });
      await batch.commit();
      return { driverId, listingId };
    } catch (e: any) {
      return rejectWithValue(e.message ?? "Failed to set active vehicle");
    }
  }
);

export const fetchUserTrips = createAsyncThunk<
  ActiveTrip[],
  { userId: string; role?: "rider" | "driver" }
>(
  "drivers/fetchUserTrips",
  async ({ userId, role = "rider" }) => {
    const field = role === "driver" ? "driverId" : "riderId";
    const snap  = await getDocs(
      query(
        collection(db, "rideTrips"),
        where(field, "==", userId),
        orderBy("createdAt", "desc"),
        limit(20)
      )
    );
    return snap.docs.map((d) => toActiveTrip(d.data()));
  }
);

export const fetchOperatorsByUserId = createAsyncThunk<any[], string, { rejectValue: string }>(
  "drivers/fetchOperatorsByUserId",
  async (driverId, { rejectWithValue }) => {
    try {
      const snap = await getDocs(
        query(collection(db, "operators"), where("driverId", "==", driverId))
      );
      return snap.docs.map((d) => ({ operatorId: d.id, ...d.data() }));
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

/* ─────────────────────────────────────────────────────────────────────────── */
/* LISTENERS                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * listenToDriverData
 * Queries by driverId field (= user UID). Real-time — this is the source of
 * truth `DriverCars` should read `listings` from, instead of one-off getDocs.
 */
export const listenToDriverData =
  (driverId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!driverId) return;
    const q = query(collection(db, "riders"), where("driverId", "==", driverId));
    return onSnapshot(
      q,
      (snap) => {
        const listings = snap.docs.map((d) => parseDriver(d.id, d.data()));
        dispatch(setListings(listings));
        dispatch(setCurrentDriver(listings.find((l) => l.isSelected) ?? listings[0] ?? null));
      },
      (e) => dispatch(setError(e.message))
    );
  };

export const listenToAllDrivers =
  (operatorId?: string) =>
  (dispatch: AppDispatch): (() => void) => {
    const constraints: any[] = [where("isPublished", "==", true)];
    if (operatorId) constraints.push(where("operatorId", "==", operatorId));
    const q = query(collection(db, "riders"), ...constraints);
    return onSnapshot(
      q,
      (snap) => dispatch(setAllDrivers(snap.docs.map((d) => parseDriver(d.id, d.data())))),
      (e)    => dispatch(setError(e.message))
    );
  };

export const listenToDriverTripRequests =
  (driverId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!driverId) return;
    const r = dbRef(rtdb, `driverRequests/${driverId}`);
    const handler = onValue(r, (snap) => {
      snap.forEach((child) => {
        const val = child.val();
        if (val) dispatch(setPendingTripRequest({ ...val, tripKey: child.key }));
      });
    });
    return () => off(r, "value", handler);
  };

export const listenToDriverParcelRequests =
  (driverId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!driverId) return;
    const r = dbRef(rtdb, `driverParcelRequests/${driverId}`);
    const handler = onValue(r, (snap) => {
      snap.forEach((child) => {
        const val = child.val();
        if (val) dispatch(setPendingParcelRequest({ ...val, parcelKey: child.key }));
      });
    });
    return () => off(r, "value", handler);
  };

export const listenToActiveTrip =
  (tripId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!tripId) return;
    return onSnapshot(
      doc(db, "rideTrips", tripId),
      (snap) => {
        if (snap.exists()) dispatch(setActiveTrip(toActiveTrip(snap.data())));
        else               dispatch(setActiveTrip(null));
      },
      (e) => dispatch(setError(e.message))
    );
  };

export const listenToRiderTripStatus =
  (riderId: string, tripId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!riderId || !tripId) return;
    const r = dbRef(rtdb, `riderTrips/${riderId}/${tripId}`);
    const handler = onValue(
      r,
      (snap) => { const val = snap.val(); if (val) dispatch(updateActiveTripFields(val)); },
      (e)    => dispatch(setError(e.message))
    );
    return () => off(r, "value", handler);
  };

export const listenToOperator =
  (operatorId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!operatorId) return;
    return onSnapshot(doc(db, "operators", operatorId), (snap) => {
      if (snap.exists()) console.log("[listenToOperator] updated:", operatorId);
    });
  };

/* ─────────────────────────────────────────────────────────────────────────── */
/* SLICE                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

const driversSlice = createSlice({
  name: "drivers",
  initialState,
  reducers: {
    setCurrentDriver: (state, action: PayloadAction<DriverProfile | null>) => {
      state.currentDriver = action.payload;
    },
    setAllDrivers: (state, action: PayloadAction<DriverProfile[]>) => {
      state.allDrivers = action.payload;
    },
    setListings: (state, action: PayloadAction<DriverProfile[]>) => {
      state.listings = action.payload;
    },
    setActiveTrip: (state, action: PayloadAction<any>) => {
      state.activeTrip = action.payload as ActiveTrip | null;
    },
    updateActiveTripFields: (state, action: PayloadAction<Partial<ActiveTrip>>) => {
      if (state.activeTrip) state.activeTrip = { ...state.activeTrip, ...action.payload };
    },
    setActiveParcel: (state, action: PayloadAction<ActiveParcel | null>) => {
      state.activeParcel = action.payload;
    },
    setPendingTripRequest:     (state, action: PayloadAction<any>) => { state.pendingTripRequest    = action.payload; },
    clearPendingTripRequest:   (state)                             => { state.pendingTripRequest    = null; },
    setPendingParcelRequest:   (state, action: PayloadAction<any>) => { state.pendingParcelRequest  = action.payload; },
    clearPendingParcelRequest: (state)                             => { state.pendingParcelRequest  = null; },
    setError:   (state, action: PayloadAction<string>) => { state.error = action.payload; state.loading = false; },
    setLoading: (state, action: PayloadAction<boolean>) => { state.loading = action.payload; },
    clearActiveTrip: (state) => { state.activeTrip = null; },
    clearAll: (state) => {
      state.currentDriver        = null;
      state.allDrivers           = [];
      state.listings             = [];
      state.activeTrip           = null;
      state.activeParcel         = null;
      state.pendingTripRequest   = null;
      state.pendingParcelRequest = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(upsertDriver.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(upsertDriver.fulfilled, (state, action) => {
        state.loading       = false;
        state.currentDriver = action.payload;
        // sync into allDrivers
        const ai = state.allDrivers.findIndex((d) => d.listingId === action.payload.listingId);
        if (ai >= 0) state.allDrivers[ai] = action.payload; else state.allDrivers.push(action.payload);
        // sync into listings
        const li = state.listings.findIndex((d) => d.listingId === action.payload.listingId);
        if (li >= 0) state.listings[li] = action.payload; else state.listings.push(action.payload);
      })
      .addCase(upsertDriver.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? "Failed";
      });

    builder.addCase(upsertDriverPhotos.fulfilled, (state, action) => {
      if (state.currentDriver) state.currentDriver.photos = action.payload;
    });

    builder.addCase(deleteDriver.fulfilled, (state, action) => {
      state.allDrivers = state.allDrivers.filter((d) => d.listingId !== action.payload);
      state.listings   = state.listings.filter((d)   => d.listingId !== action.payload);
      if (state.currentDriver?.listingId === action.payload) state.currentDriver = null;
    });

    builder.addCase(setActiveVehicle.fulfilled, (state, action) => {
      const { listingId } = action.payload;
      state.listings   = state.listings.map((d)   => ({ ...d, isSelected: d.listingId === listingId }));
      state.allDrivers = state.allDrivers.map((d) => ({ ...d, isSelected: d.listingId === listingId }));
      if (state.currentDriver) {
        state.currentDriver = state.listings.find((d) => d.listingId === state.currentDriver!.listingId)
          ?? state.currentDriver;
      }
    });

    builder.addCase(fetchUserTrips.fulfilled,        () => {});
    builder.addCase(fetchOperatorsByUserId.fulfilled, () => {});
  },
});

export default driversSlice.reducer;

export const {
  setCurrentDriver,
  setListings,
  setAllDrivers,
  setActiveTrip,
  updateActiveTripFields,
  setActiveParcel,
  setPendingTripRequest,
  clearPendingTripRequest,
  setPendingParcelRequest,
  clearPendingParcelRequest,
  setError,
  setLoading,
  clearActiveTrip,
  clearAll,
} = driversSlice.actions;

export const selectCurrentDriver        = (s: RootState) => s.drivers.currentDriver;
export const selectListings             = (s: RootState) => s.drivers.listings;
export const selectAllDrivers           = (s: RootState) => s.drivers.allDrivers;
export const selectActiveTrip           = (s: RootState) => s.drivers.activeTrip;
export const selectActiveParcel         = (s: RootState) => s.drivers.activeParcel;
export const selectPendingTripRequest   = (s: RootState) => s.drivers.pendingTripRequest;
export const selectPendingParcelRequest = (s: RootState) => s.drivers.pendingParcelRequest;
export const selectDriversLoading       = (s: RootState) => s.drivers.loading;
export const selectDriversError         = (s: RootState) => s.drivers.error;
export const selectOperators            = (s: RootState) => s.drivers.allDrivers;
export const selectOperatorLoading      = (s: RootState) => s.drivers.loading;